/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Returns true if Fullstory is installed correctly.
 */
function doesFullStoryExist() {
    return !!window[window['_fs_namespace']];
}
/**
 * Get the project ID from a Sentry DSN
 * @param {string} dsn
 */
var getProjectIdFromSentryDsn = function (dsn) {
    return new URL(dsn).pathname.replace('/', '');
};
var isError = function (exception) {
    return exception.message !== undefined;
};
/**
 * Get the message and name properties from the original exception
 * @param {EventHint} hint
 */
var getOriginalExceptionProperties = function (hint) {
    if (hint && hint.originalException && isError(hint.originalException)) {
        var originalException = hint.originalException;
        var name_1 = originalException.name, message = originalException.message;
        return { name: name_1, message: message };
    }
    return {};
};
/**
 * Returns the sentry URL of the error. If we cannot get the URL, return a
 * string saying we cannot.
 */
function getSentryUrl(_a) {
    var _b;
    var hint = _a.hint, sentryOrg = _a.sentryOrg, baseSentryUrl = _a.baseSentryUrl, hub = _a.hub;
    try {
        // No docs on this but the SDK team assures me it works unless you bind another Sentry client
        var dsn = (((_b = hub.getClient()) === null || _b === void 0 ? void 0 : _b.getOptions()) || {}).dsn;
        if (!dsn) {
            console.error('No sn');
            return 'Could not retrieve url';
        }
        if (!hint) {
            console.error('No event hint');
            return 'Could not retrieve url';
        }
        var projectId = getProjectIdFromSentryDsn(dsn);
        return baseSentryUrl + "/organizations/" + sentryOrg + "/issues/?project=" + projectId + "&query=" + hint.event_id;
    }
    catch (err) {
        console.error('Error retrieving project ID from DSN', err);
        //TODO: Could put link to a help here
        return 'Could not retrieve url';
    }
}

var SentryFullStory = /** @class */ (function () {
    function SentryFullStory(sentryOrg, options) {
        var _this = this;
        this.name = SentryFullStory.id;
        this.getFullStoryUrl = function () {
            var _a, _b;
            // getCurrentSessionURL isn't available until after the FullStory script is fully bootstrapped.
            // If an error occurs before getCurrentSessionURL is ready, make a note in Sentry and move on.
            // More on getCurrentSessionURL here: https://help.fullstory.com/develop-js/getcurrentsessionurl
            try {
                var res = (_b = (_a = _this.client).getCurrentSessionURL) === null || _b === void 0 ? void 0 : _b.call(_a, true);
                if (!res) {
                    throw new Error('No FullStory session URL found');
                }
                return res;
            }
            catch (e) {
                var reason = e instanceof Error ? e.message : String(e);
                throw new Error("Unable to get url: " + reason);
            }
        };
        this.sentryOrg = sentryOrg;
        this.client = options.client;
        this.baseSentryUrl = options.baseSentryUrl || 'https://sentry.io';
    }
    SentryFullStory.prototype.setupOnce = function (addGlobalEventProcessor, getCurrentHub) {
        var _this = this;
        var fullStoryUrl;
        addGlobalEventProcessor(function (event, hint) { return __awaiter(_this, void 0, void 0, function () {
            var hub, self, e_1, reason;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        hub = getCurrentHub();
                        self = hub.getIntegration(SentryFullStory);
                        if (!(self && event.type !== 'transaction' && doesFullStoryExist())) return [3 /*break*/, 5];
                        if (!!fullStoryUrl) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.getFullStoryUrl()];
                    case 2:
                        fullStoryUrl = _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        reason = e_1 instanceof Error ? e_1.message : String(e_1);
                        console.error("Unable to get FullStory session URL: " + reason);
                        return [3 /*break*/, 4];
                    case 4:
                        if (fullStoryUrl) {
                            event.contexts = __assign(__assign({}, event.contexts), { fullStory: {
                                    fullStoryUrl: fullStoryUrl,
                                } });
                        }
                        try {
                            this.client.event('Sentry Error', __assign({ sentryUrl: getSentryUrl({
                                    baseSentryUrl: this.baseSentryUrl,
                                    sentryOrg: this.sentryOrg,
                                    hint: hint,
                                    hub: hub,
                                }) }, getOriginalExceptionProperties(hint)));
                        }
                        catch (e) {
                            console.debug('Unable to report sentry error details to FullStory');
                        }
                        _a.label = 5;
                    case 5: return [2 /*return*/, event];
                }
            });
        }); });
    };
    SentryFullStory.id = 'SentryFullStory';
    return SentryFullStory;
}());

export default SentryFullStory;
